import "moment-timezone";
import moment from "moment-timezone";
import { useEffect, useRef, useState, useCallback } from "react";
import Image from "next/image";
import { Newspaper, Calendar } from "lucide-react";
import { fetchWrapper } from "src/helpers";
import {
  getLocalStorageUser,
  toTitleCase,
  getInitials,
} from "src/helpers/api/sharedFunction";

interface PestelNewsArticle {
  id: string;
  stock_symbol_id: string;
  is_stock_available: boolean;
  company: string;
  title: string;
  description: string;
  category: string;
  source: string;
  url: string | null;
  published_at: string;
  created_at: string;
  impact: string;
  sentiment: string;
  sub_categories: string[];
}

interface ApiNewsItem {
  Symbol: string;
  is_stock_available: boolean;
  Company: string;
  company_name: string;
  Description: string;
  News_Tag?: string;
  Source: string;
  Link?: string | null;
  created_at: string;
  Sentiment?: string;
  Stock_Impact?: string;
  Subcategories?: string;
}

const CATEGORIES = [
  { value: "All", label: "All" },
  { value: "Political", label: "Political" },
  { value: "Environmental", label: "Environmental" },
  { value: "Social", label: "Social" },
  { value: "Technological", label: "Technological" },
  { value: "Economic", label: "Economic" },
  { value: "Legal", label: "Legal" },
  { value: "Others", label: "Others" },
];

export const SENTIMENT_STYLES: Record<string, { text: string }> = {
  Positive: { text: "text-green-600 dark:text-[#00E676]" },
  Negative: { text: "text-red-600 dark:text-[#FF5252]" },
  Neutral: { text: "text-gray-600 dark:text-gray-400" },
  None: { text: "text-gray-600 dark:text-gray-400" },
};

export const getImpactColor = (impact: string) => {
  switch (impact) {
    case "High":
      return "text-red-600 dark:text-[#FF5252]";
    case "Medium":
      return "text-yellow-600 dark:text-yellow-400";
    case "Low":
      return "text-green-600 dark:text-[#00E676]";
    default:
      return "text-gray-600 dark:text-gray-400";
  }
};

export default function CompanyNewsTab({
  selectedCompany,
}: {
  selectedCompany: string;
}) {
  const [activeTab, setActiveTab] = useState("All");
  const [sentimentFilter, setSentimentFilter] = useState("All");
  const [news, setNews] = useState<PestelNewsArticle[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>(
    {},
  );
  const [sentimentCounts, setSentimentCounts] = useState<
    Record<string, number>
  >({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const observerTarget = useRef(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchNews = useCallback(
    async (pageNum: number = 1, currentTab: string) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setLoading(true);
      try {
        const user = getLocalStorageUser();
        const token = user?.token;

        if (!token) {
          setLoading(false);
          return;
        }

        const queryCategory = currentTab === "All" ? "" : currentTab;

        const queryParams = new URLSearchParams({
          symbol: selectedCompany,
          category: queryCategory,
          stock_impact: "",
          sentiment: sentimentFilter === "All" ? "" : sentimentFilter,
          preference: "",
          days: "30",
          limit: "20",
          page: pageNum.toString(),
        });

        const response = await fetchWrapper.get(
          `/api/dashboard/news?${queryParams.toString()}`,
          undefined,
          { Authorization: `Bearer ${token}` },
          controller.signal,
        );

        const apiData = response.data || [];
        const formattedNews: PestelNewsArticle[] = [];

        apiData.forEach((item: ApiNewsItem) => {
          let itemCategories = item.News_Tag
            ? item.News_Tag.split(",").map((c: string) => c.trim())
            : ["Others"];
          const normalizedCategories = itemCategories.map((c: string) =>
            c === "Economical" ? "Economic" : c,
          );
          const displayCategory =
            currentTab === "All"
              ? normalizedCategories[0] || "Others"
              : currentTab;

          formattedNews.push({
            id: `${item.Symbol}-${item.created_at}-${displayCategory}-${Math.random()}`,
            stock_symbol_id: item.Symbol,
            is_stock_available: item.is_stock_available,
            company: item.Company,
            title: item.company_name,
            description: item.Description || "",
            category: displayCategory,
            source: item.Source || "Unknown",
            url: item.Link || null,
            published_at: item.created_at,
            created_at: item.created_at,
            sentiment: toTitleCase(item.Sentiment || "None"),
            impact: toTitleCase(item.Stock_Impact || "None"),
            sub_categories: item.Subcategories
              ? item.Subcategories.split(",").map((c: string) =>
                  toTitleCase(c.trim()),
                )
              : [],
          });
        });

        if (pageNum === 1) {
          setNews(formattedNews);
        } else {
          setNews((prev) => [...prev, ...formattedNews]);
        }

        if (response.counts) {
          setCategoryCounts(response.counts);
        }
        if (response.sentimentCounts) {
          setSentimentCounts(response.sentimentCounts);
        }

        if (response.pagination) {
          setHasMore(response.pagination.page < response.pagination.totalPages);
        } else {
          setHasMore(apiData.length === 20);
        }
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          console.log("Fetch aborted");
        } else {
          console.error("Error fetching news:", error);
        }
      } finally {
        if (abortControllerRef.current === controller) {
          setLoading(false);
        }
      }
    },
    [selectedCompany, sentimentFilter],
  );

  useEffect(() => {
    setPage(1);
    fetchNews(1, activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, sentimentFilter, selectedCompany]);

  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchNews(nextPage, activeTab);
    }
  }, [loading, hasMore, page, activeTab, fetchNews]);

  useEffect(() => {
    const currentTarget = observerTarget.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && hasMore) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 },
    );

    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [loading, hasMore, handleLoadMore]);

  return (
    <div className="flex flex-col h-[600px] w-full overflow-hidden relative bg-transparent">
      {/* Category and Sentiment Header */}
      <div className="flex-shrink-0 border-b border-[#d0d4e4] dark:border-gray-700 p-2 sm:p-4">
        <div className="flex w-full items-center justify-between gap-2 overflow-hidden">
          {/* Categories */}
          <div className="flex items-center gap-1 sm:gap-2 overflow-hidden">
            {CATEGORIES.map((category) => {
              const count = Number(categoryCounts[category.value] || 0);

              const isActive = activeTab === category.value;

              if (category.value === "All") {
                return (
                  <button
                    key={category.value}
                    onClick={() => setActiveTab(category.value)}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all whitespace-nowrap ${
                      isActive
                        ? "cursor-pointer bg-[#00A85F] text-white"
                        : "cursor-pointer bg-transparent border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                    }`}
                  >
                    All
                  </button>
                );
              }

              return (
                <button
                  key={category.value}
                  onClick={() => count > 0 && setActiveTab(category.value)}
                  disabled={count === 0}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all whitespace-nowrap flex items-center gap-1 sm:gap-1.5 ${
                    count === 0
                      ? "cursor-not-allowed opacity-50 bg-transparent border border-gray-300 dark:border-gray-700 text-gray-400 dark:text-gray-500"
                      : isActive
                        ? "cursor-pointer bg-[#00A85F] text-white border border-[#00A85F]"
                        : "cursor-pointer bg-transparent border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                  }`}
                >
                  {category.label}
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                      isActive
                        ? "bg-white/20 text-white"
                        : "bg-gray-200 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Sentiment Filter */}
          <div className="flex items-center gap-2 sm:gap-3 text-xs font-medium whitespace-nowrap flex-shrink-0">
            <span className="text-gray-700 dark:text-gray-300 hidden xl:inline">
              Sentiment:
            </span>

            <button
              onClick={() =>
                setSentimentFilter(
                  sentimentFilter === "Positive" ? "All" : "Positive",
                )
              }
              className={`transition-colors cursor-pointer flex items-center gap-1 hover:text-green-700 dark:hover:text-green-400 ${sentimentFilter === "Positive" ? "font-bold text-green-700 dark:text-green-400" : "text-green-600 dark:text-green-500"}`}
            >
              Positive{" "}
              <span className="opacity-80">
                ({sentimentCounts["Positive"] || 0})
              </span>
            </button>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <button
              onClick={() =>
                setSentimentFilter(
                  sentimentFilter === "Negative" ? "All" : "Negative",
                )
              }
              className={`transition-colors cursor-pointer flex items-center gap-1 hover:text-red-700 dark:hover:text-red-400 ${sentimentFilter === "Negative" ? "font-bold text-red-700 dark:text-red-400" : "text-red-600 dark:text-red-500"}`}
            >
              Negative{" "}
              <span className="opacity-80">
                ({sentimentCounts["Negative"] || 0})
              </span>
            </button>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <button
              onClick={() =>
                setSentimentFilter(
                  sentimentFilter === "Neutral" ? "All" : "Neutral",
                )
              }
              className={`transition-colors cursor-pointer flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-300 ${sentimentFilter === "Neutral" ? "font-bold text-gray-700 dark:text-gray-300" : "text-gray-500 dark:text-gray-400"}`}
            >
              Neutral{" "}
              <span className="opacity-80">
                ({sentimentCounts["Neutral"] || 0})
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* News List */}
      <div
        className="flex-1 overflow-y-auto p-4 pb-12 custom-scroll"
        id="company-news-scroll"
      >
        {loading && news.length === 0 ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
          </div>
        ) : news.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Newspaper className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-xl font-semibold text-black dark:text-white mb-2">
              No news found
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              There are no{" "}
              {sentimentFilter === "All"
                ? ""
                : sentimentFilter.toLowerCase() + " "}
              {activeTab === "All" ? "" : activeTab.toLowerCase() + " "}
              news items for the selected time period
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {news.map((item) => {
              const normalizedSentiment = item.sentiment;
              const normalizedImpact = item.impact;
              const sentimentStyle =
                SENTIMENT_STYLES[normalizedSentiment] ||
                SENTIMENT_STYLES["None"];
              const impactStyle = getImpactColor(normalizedImpact);

              return (
                <div
                  key={item.id}
                  className="relative py-4 border-b border-gray-200 dark:border-gray-700/60 last:border-0 hover:bg-gray-50/50 dark:hover:bg-gray-800/20 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    {/* Left: Image / Logo */}
                    <div className="w-12 h-12 rounded-[10px] flex-shrink-0 flex items-center justify-center text-black dark:text-white font-bold text-lg bg-white dark:bg-white border border-gray-200 dark:border-gray-300 overflow-hidden shadow-sm">
                      {item.is_stock_available ? (
                        <Image
                          src={`https://firebasestorage.googleapis.com/v0/b/abillion-analytics.appspot.com/o/${item.stock_symbol_id}.jpg?alt=media`}
                          alt={item.company}
                          width={44}
                          height={44}
                          unoptimized
                          className="w-full h-full object-contain p-1"
                        />
                      ) : (
                        <>{getInitials(item.stock_symbol_id)}</>
                      )}
                    </div>

                    {/* Right: Content */}
                    <div className="flex-1 min-w-0">
                      {/* Top Row: Title and Badges */}
                      <div className="flex items-start justify-between gap-4">
                        <h3 className="text-[15px] font-bold text-gray-900 dark:text-white leading-snug">
                          {item.title}
                        </h3>

                        {/* Top Right Labels */}
                        <div className="flex items-center gap-4 flex-shrink-0 text-[13px] font-medium tracking-wide">
                          <div className="text-gray-500 dark:text-gray-400">
                            Impact:{" "}
                            <span className={impactStyle}>
                              {normalizedImpact}
                            </span>
                          </div>
                          <div className="text-gray-500 dark:text-gray-400">
                            Sentiment:{" "}
                            <span className={sentimentStyle.text}>
                              {normalizedSentiment}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Description */}
                      <div className="relative mt-1 mb-2">
                        <p
                          ref={(el) => {
                            if (el) {
                              const isOverflowing =
                                el.scrollHeight > el.clientHeight;
                              if (isOverflowing && !(item.id in expanded)) {
                                setExpanded((prev) => ({
                                  ...prev,
                                  [item.id]: false,
                                }));
                              }
                            }
                          }}
                          className={`text-gray-500 dark:text-gray-400 text-[13px] leading-relaxed ${!expanded[item.id] ? "line-clamp-2" : ""}`}
                        >
                          {item.description}
                        </p>
                        {expanded[item.id] !== undefined && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              setExpanded((prev) => ({
                                ...prev,
                                [item.id]: !prev[item.id],
                              }));
                            }}
                            className="text-[11px] font-semibold text-[#00A85F] hover:underline cursor-pointer mt-1"
                          >
                            {expanded[item.id] ? "Read less" : "Read more"}
                          </button>
                        )}
                      </div>

                      {/* Bottom Meta Data */}
                      <div className="flex flex-wrap items-center gap-5 mt-3 text-[13px] font-medium text-gray-500 dark:text-gray-400">
                        {item.sub_categories &&
                          item.sub_categories.length > 0 && (
                            <div className="flex items-center gap-2">
                              <span>Sub Categories:</span>
                              {item.sub_categories.map((subCat, idx) => (
                                <span
                                  key={idx}
                                  className="px-3 py-1 rounded-full text-[13px] font-medium bg-blue-50 dark:bg-[#1A2947] text-blue-600 dark:text-[#4B8CFF]"
                                >
                                  {subCat}
                                </span>
                              ))}
                            </div>
                          )}

                        <div className="flex items-center gap-1.5">
                          <Newspaper className="w-4 h-4" />
                          <span>{item.source}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {moment(item.published_at).format(
                              "MMM D, YYYY, h:mm A",
                            )}{" "}
                            (IST)
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            <div
              ref={observerTarget}
              className="h-10 flex justify-center items-center"
            >
              {loading && news.length > 0 && (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 dark:border-white"></div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
